import '#src/chartjs.ts';

import type { Private } from '@seek/indie-api-types';
import { useColor } from 'braid-design-system';
import { enAU } from 'date-fns/locale';
import { Line } from 'react-chartjs-2';

import { useLazyColorMode } from '#src/components/ColorModeProvider/ColorModeProvider.tsx';
import type { ComponentProps } from 'react';

type Unit = 'day' | 'hour' | 'minute';

const getDateUnit = (
  timeRange: Private.WebhookSubscriptionRequestMetricTimeRange,
): Unit => {
  switch (timeRange) {
    case 'weeks3':
      return 'day';
    case 'days3':
      return 'hour';
    case 'hours3':
      return 'minute';
  }
};

const getDateValueMap = (values: Array<{ value: number; date: string }>) =>
  new Map(values.map(({ value, date }) => [date, value]));

interface Props {
  timeRange: Private.WebhookSubscriptionRequestMetricTimeRange;
  datasets: Private.WebhookSubscriptionRequestMetric['data']['values'];
}

export const ResponseGraph = ({
  datasets: { BadResponse, InvalidUrl, RequestTimeout, Success },
  timeRange,
}: Props) => {
  const color = useColor();
  const lazyColorMode = useLazyColorMode();

  const labels = Array.from(
    new Set(
      [BadResponse, InvalidUrl, RequestTimeout, Success]
        .map((dataset) => dataset.map(({ date }) => date))
        .flat(),
    ),
  ).sort((a, b) => new Date(a).getTime() - new Date(b).getTime());

  const badResponseMap = getDateValueMap(BadResponse);
  const invalidUrlMap = getDateValueMap(InvalidUrl);
  const requestTimeOutMap = getDateValueMap(RequestTimeout);
  const successMap = getDateValueMap(Success);

  const gridColor =
    lazyColorMode?.colorMode === 'darkMode'
      ? color.background.neutral
      : color.background.neutralLight;

  const textColor =
    lazyColorMode?.colorMode === 'darkMode'
      ? color.foreground.secondaryInverted
      : color.foreground.secondary;

  const options = {
    responsive: true,
    spanGaps: true,
    scales: {
      x: {
        adapters: { date: { locale: enAU } },
        grid: { color: gridColor },
        ticks: { color: textColor },
        time: { unit: getDateUnit(timeRange) },
        type: 'time' as const,
      },
      y: {
        beginAtZero: true,
        grid: { color: gridColor },
        ticks: { color: textColor },
      },
    },
    plugins: {
      legend: {
        labels: { color: textColor },
        position: 'bottom' as const,
      },
    },
  } satisfies ComponentProps<typeof Line>['options'];

  const data = {
    labels,
    datasets: [
      {
        label: 'Success',
        data: labels.map((label) => successMap.get(label)),
        borderColor: color.foreground.positiveLight,
        backgroundColor: color.background.positiveLight,
      },
      {
        label: 'Bad Response',
        data: labels.map((label) => badResponseMap.get(label)),
        borderColor: color.foreground.criticalLight,
        backgroundColor: color.background.criticalLight,
      },
      {
        label: 'Request Timeout',
        data: labels.map((label) => requestTimeOutMap.get(label)),
        borderColor: color.foreground.brandAccent,
        backgroundColor: color.background.criticalLight,
      },
      {
        label: 'Invalid URL',
        data: labels.map((label) => invalidUrlMap.get(label)),
        borderColor: color.foreground.cautionLight,
        backgroundColor: color.background.cautionLight,
      },
    ],
  } satisfies ComponentProps<typeof Line>['data'];

  return <Line options={options} data={data} />;
};
